/**
 * Report printing, the parts that are not React (owner, 2026-10-26).
 *
 * A report to print is a document: the columns the owner kept, every row as the
 * screen formatted it, and the server's own totals. Thermal is laid out by the
 * server (it knows the printer's paper width and sends back the exact slip as the
 * preview). A4 is laid out here, as one HTML page that is both the preview and
 * what the browser prints — the same iframe, so the two cannot differ.
 */

const escapeHtml = value =>
  String(value ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);

/** The document with only the chosen columns, in the report's own order. */
export function pickColumns(doc, keys) {
  const keep = doc.columns.map((column, index) => (keys.includes(column.key) ? index : -1)).filter(index => index >= 0);
  return {
    ...doc,
    columns: keep.map(index => doc.columns[index]),
    rows: doc.rows.map(row => keep.map(index => row[index])),
    // The first kept cell carries the "Total" label only when it holds no figure:
    // with the name column unticked, the first column left may be a number.
    totals: doc.totals ? keep.map((index, position) => (position === 0 ? doc.totals[index] || "Total" : doc.totals[index])) : null,
  };
}

const FONT_PT = { small: 8, normal: 9.5, fit: 9.5 };
const MARGIN_MM = 12;
const PAGE_MM = { portrait: 210, landscape: 297 };
// "Fit page" never shrinks below this: smaller is not readable on paper.
const MIN_ZOOM = 0.5;

/**
 * Lay the page out in the browser and measure it: does the table fit across the
 * page, and — for "Fit page" — shrink the content until it does. Run on the very
 * document that prints (the preview iframe), so the warning and the shrink use
 * the real rendered widths, not a guess from character counts.
 */
export function fitPage(pageDocument, { fit = false } = {}) {
  const content = pageDocument?.querySelector(".content");
  const table = content?.querySelector("table");
  if (!content || !table) return { overflow: false, zoom: 1 };
  const widthMm = Number(content.dataset.widthMm);
  content.style.zoom = "1";
  content.style.width = `${widthMm}mm`;
  let zoom = 1;
  if (fit && table.scrollWidth > content.clientWidth) {
    zoom = Math.max(MIN_ZOOM, (content.clientWidth / table.scrollWidth) * 0.98);
    // Laid out wider by 1/zoom, then zoomed back down to exactly the page.
    content.style.width = `${(widthMm / zoom).toFixed(1)}mm`;
    content.style.zoom = zoom.toFixed(3);
  }
  return { overflow: table.scrollWidth > content.clientWidth + 1, zoom };
}

/** The whole A4 page: preview in the iframe, and what the browser prints. */
export function a4Html(doc, { orientation = "portrait", size = "normal", restaurant = "", printedAt = "" } = {}) {
  const numeric = doc.columns.map(column => column.numeric);
  const cell = (value, index, tag = "td") =>
    `<${tag}${numeric[index] ? ' class="num"' : ""}>${escapeHtml(value) || "&nbsp;"}</${tag}>`;
  const head = `<tr>${doc.columns.map((column, index) => cell(column.label, index, "th")).join("")}</tr>`;
  const body = doc.rows.map(row => `<tr>${row.map((value, index) => cell(value, index)).join("")}</tr>`).join("");
  const totals = doc.totals
    ? `<tr class="totals">${doc.totals.map((value, index) => cell(value, index)).join("")}</tr>`
    : "";
  const extra = doc.extraLines?.length
    ? `<table class="extra">${doc.extraLines.map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td class="num">${escapeHtml(value)}</td></tr>`).join("")}</table>`
    : "";
  const width = PAGE_MM[orientation] - 2 * MARGIN_MM;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(doc.title)}</title><style>
@page { size: A4 ${orientation}; margin: ${MARGIN_MM}mm; }
* { box-sizing: border-box; }
body { margin: 0; font-family: system-ui, "Segoe UI", Arial, sans-serif; font-size: ${FONT_PT[size] || FONT_PT.normal}pt; color: #111; }
header { margin-bottom: 4mm; }
header .name { font-size: 9pt; color: #555; }
h1 { font-size: 14pt; margin: 1mm 0; }
header .meta { font-size: 8pt; color: #555; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
tr { break-inside: avoid; }
th, td { padding: 1.2mm 1.25mm; text-align: left; vertical-align: top; border-bottom: 0.4pt solid #ccc; overflow-wrap: break-word; }
.content { width: ${width}mm; }
th { font-weight: 600; border-bottom: 0.8pt solid #333; }
.num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
tr.totals td { font-weight: 700; border-top: 0.8pt solid #333; border-bottom: none; }
table.extra { width: auto; margin-top: 4mm; }
table.extra td { border: none; padding: 0.6mm 4mm 0.6mm 0; }
footer { margin-top: 4mm; font-size: 8pt; color: #555; }
@media screen {
  html { background: #e4e4e7; }
  body { width: ${PAGE_MM[orientation]}mm; min-height: ${orientation === "portrait" ? 297 : 210}mm; margin: 16px auto; padding: ${MARGIN_MM}mm; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.2); }
}
</style></head><body><div class="content" data-width-mm="${width}">
<header>${restaurant ? `<div class="name">${escapeHtml(restaurant)}</div>` : ""}<h1>${escapeHtml(doc.title)}</h1>
<div class="meta">${[doc.subtitle, printedAt && `Printed ${printedAt}`].filter(Boolean).map(escapeHtml).join(" · ")}</div></header>
<table><thead>${head}</thead><tbody>${body}${totals}</tbody></table>
${extra}
<footer>${doc.rows.length} row(s)</footer>
</div></body></html>`;
}
